import { useRef, type MouseEvent as ReactMouseEvent } from 'react'

/**
 * Click-and-drag horizontal scrolling for overflow containers, so the sliders
 * are usable with a mouse on desktop and not just via trackpad or scrollbar.
 *
 * Attach `scrollRef` and `onMouseDown` to the scroll container. Pass
 * `handleLinkClick` to links inside it, and gate button handlers on
 * `wasDragged()` so a drag never triggers the card underneath.
 */
export function useDragScroll() {
  const scrollRef = useRef<HTMLDivElement>(null)
  const hasDraggedRef = useRef(false)

  const onMouseDown = (e: ReactMouseEvent) => {
    e.preventDefault()
    const el = scrollRef.current
    if (!el) return

    const startX = e.pageX
    const scrollLeft = el.scrollLeft
    hasDraggedRef.current = false
    el.style.cursor = 'grabbing'

    const handleMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault()
      const walk = (moveEvent.pageX - startX) * 1.5
      if (Math.abs(walk) > 5) {
        hasDraggedRef.current = true
      }
      el.scrollLeft = scrollLeft - walk
    }

    const handleMouseUp = () => {
      el.style.cursor = 'grab'
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const handleLinkClick = (e: ReactMouseEvent) => {
    if (hasDraggedRef.current) {
      e.preventDefault()
    }
  }

  const wasDragged = () => hasDraggedRef.current

  return { scrollRef, onMouseDown, handleLinkClick, wasDragged }
}
